import React from 'react';
import { streamQuerySeriesResults } from '../utils/streamQuerySeriesResults';

const createRowId = (groupResultId, rowIndex) => `${groupResultId}_${rowIndex}`;

export const useQuerySeriesResults = () => {
  const streamRef = React.useRef(null);
  const [resultLookup, setResultLookup] = React.useState({});
  const [completedRowLookup, setCompletedRowLookup] = React.useState({});
  const [isSeriesComplete, setIsSeriesComplete] = React.useState(false);
  const [streamError, setStreamError] = React.useState(null);

  const cancelStream = React.useCallback(() => {
    streamRef.current?.cancel?.();
    streamRef.current = null;
  }, []);

  const resetResults = React.useCallback(() => {
    cancelStream();
    setResultLookup({});
    setCompletedRowLookup({});
    setIsSeriesComplete(false);
    setStreamError(null);
  }, [cancelStream]);

  const startStream = React.useCallback(
    ({ groupResultId, onRowMessage, onSeriesComplete, onSeriesError }) => {
      cancelStream();
      setIsSeriesComplete(false);
      setStreamError(null);

      streamRef.current = streamQuerySeriesResults({
        resultId: groupResultId,
        onMessage: ({ rowIndex, message }) => {
          const rowId = createRowId(groupResultId, rowIndex);
          setResultLookup((prev) => ({
            ...prev,
            [rowId]: { ...(prev[rowId] || {}), rawMessage: message },
          }));
          onRowMessage?.({ rowIndex, message });
        },
        onComplete: ({ answersWithContexts }) => {
          setResultLookup((prev) => {
            const nextResults = { ...prev };
            answersWithContexts.forEach((item, rowIndex) => {
              const rowId = createRowId(groupResultId, rowIndex);
              nextResults[rowId] = {
                ...(nextResults[rowId] || {}),
                answer: item?.answer || '',
                rawMessage: item?.answer || nextResults[rowId]?.rawMessage || '',
              };
            });
            return nextResults;
          });
          const nextCompleted = {};
          answersWithContexts.forEach((_, rowIndex) => {
            nextCompleted[createRowId(groupResultId, rowIndex)] = true;
          });
          setCompletedRowLookup((prev) => ({ ...prev, ...nextCompleted }));
          setIsSeriesComplete(true);
          onSeriesComplete?.({ answersWithContexts });
        },
        onError: (error) => {
          setStreamError(error);
          onSeriesError?.(error);
        },
      });
    },
    [cancelStream]
  );

  React.useEffect(() => cancelStream, [cancelStream]);

  const getRowResult = React.useCallback(
    (groupResultId, rowIndex) => {
      return resultLookup[createRowId(groupResultId, rowIndex)] || null;
    },
    [resultLookup]
  );

  const isRowComplete = React.useCallback(
    (groupResultId, rowIndex) => Boolean(completedRowLookup[createRowId(groupResultId, rowIndex)]),
    [completedRowLookup]
  );

  return {
    resultLookup,
    completedRowLookup,
    streamError,
    isSeriesComplete,
    startStream,
    cancelStream,
    resetResults,
    getRowResult,
    isRowComplete,
  };
};
